import React, { useEffect, useState } from 'react';
import { Button, Tag } from 'antd';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import Loader from '../Components/Loader';
import axiosInstance from '../utils/axiosInstance';
import { formatNumber } from '../utils/numberUtil';
import { formatDate } from '../utils/dateUtil';
import { runSafely } from '../utils/runSafely';
import { useTranslation } from 'react-i18next';
import AppShell from '../Components/AppShell';
import Container from '../Components/Container';

interface OrderInfoType {
    id: number;
    referenceNo: string;
    totalAmount: number;
    comission: number;
    unit: number;
    orderStatus: string;
    createdAt: Date;
    productType: string;
    orderConfirmTimeoutSeconds?: number | null;

    User: {
        username: string;
        walletBalances: number;
    },
    Product: {
        id: number;
        name: string;
        seller: string;
        img: string;
    }
}

interface UserInfoType {
    walletBalances: number;
    clickRemaining: number;
    userOrders: any[];
    orderCountPrompt?: number;
    orderCountMessage?: string;
    userProducts: {
        id: number;
        amount: number;
        productId: number;
        name: string;
        seller: string;
        img: string;
    }[]
}

const OrderConfirmationPage: React.FC = () => {
    const [orderInfo, setOrderInfo] = useState<OrderInfoType>();
    const [userInfo, setUserInfo] = useState<UserInfoType>();

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [buttonClicked, setButtonClicked] = useState(false);
    const { t } = useTranslation();
    const [timeLeft, setTimeLeft] = useState<number | null>(null);
    const [hasTimedOut, setHasTimedOut] = useState(false);

    useEffect(() => {
        if (orderInfo?.createdAt && !buttonClicked && !hasTimedOut) {
            // Per-order countdown set by the admin on the package item. Falls back to
            // the historical 20s. Must use ?? rather than ||, so a deliberate 0 stays 0
            // instead of silently becoming 20 — and must match completeOrder's default
            // on the server, or the button dies before the server window closes.
            const limitSeconds = orderInfo.orderConfirmTimeoutSeconds ?? 20;
            if (limitSeconds === 0) {
                return;
            }

            const calculateTimeLeft = () => {
                const now = new Date();
                const orderTime = new Date(orderInfo.createdAt);
                const diffInSeconds = Math.floor((now.getTime() - orderTime.getTime()) / 1000);
                return Math.max(0, limitSeconds - diffInSeconds);
            };

            let remaining = calculateTimeLeft();
            if (remaining <= 0) {
                setTimeLeft(0);
                setHasTimedOut(true);
                Swal.fire({
                    text: 'Error! Please contact Instructor to resolve your problem',
                    icon: 'error',
                });
                return;
            }

            setTimeLeft(remaining);

            const interval = setInterval(() => {
                remaining = calculateTimeLeft();
                if (remaining <= 0) {
                    clearInterval(interval);
                    setTimeLeft(0);
                    setHasTimedOut(true);
                    Swal.fire({
                        text: 'Error! Please contact Instructor to resolve your problem',
                        icon: 'error',
                    });
                } else {
                    setTimeLeft(remaining);
                    // If it was timed out before, but now we have remaining time (because of admin reset)
                    if (hasTimedOut) {
                        setHasTimedOut(false);
                        Swal.close();
                    }
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [orderInfo?.createdAt?.getTime(), buttonClicked]);

    const completeOrder = async () => {
        if (!orderInfo) return;

        if (Number(orderInfo?.User.walletBalances ?? 0) < 0
        ) {
            Swal.fire({
                // title: 'Error!',
                text: t("balance_not_enough"),
                icon: 'error',
            }).then(() => {
                navigate("/account")
            })

            return;
        }

        setButtonClicked(true);
        const res = await axiosInstance.post("/protected/completeOrder", {
            orderId: orderInfo?.id,
        })
        if (res.status === 201) {
            const result = await createNewOrder();
            if (result) {
                if (userInfo?.userOrders.length === userInfo?.orderCountPrompt && userInfo?.orderCountMessage) {
                    Swal.fire({
                        text: userInfo.orderCountMessage,
                        icon: "warning",
                    }).then(async () => {
                        await getPendingOrder();
                        setButtonClicked(false);
                    })
                } else {
                    await getPendingOrder();
                    setButtonClicked(false);
                }
            } else {
                navigate("/order")
                setButtonClicked(false);
            }

        } else {
            if (res.data?.customize_message) {
                Swal.fire({
                    text: res.data.customize_message,
                    icon: 'error',
                });
            }
            runSafely('OrderConfirmationPage.getPendingOrder (after failure)', getPendingOrder, () => setLoading(false));
            setButtonClicked(false);
        }
    }

    const getPendingOrder = async () => {
        await getUserInfo();
        const res = await axiosInstance.get("/protected/getPendingOrder")
        const data = res.data.data;
        if (data) {
            const pendingOrder = data.pendingOrder;

            setOrderInfo({
                comission: pendingOrder.comission,
                id: pendingOrder.id,
                orderStatus: pendingOrder.orderStatus,
                referenceNo: pendingOrder.referenceNo,
                totalAmount: pendingOrder.totalAmount,
                unit: pendingOrder.unit,
                createdAt: new Date(pendingOrder.createdAt),
                productType: pendingOrder.productType,
                orderConfirmTimeoutSeconds: pendingOrder.orderConfirmTimeoutSeconds,

                User: {
                    username: pendingOrder.User.username,
                    walletBalances: pendingOrder.User.walletBalances,
                },
                Product: {
                    id: pendingOrder.Product.id,
                    img: process.env.REACT_APP_FOLDER_URL + "/" + pendingOrder.Product.img,
                    name: pendingOrder.Product.name,
                    seller: pendingOrder.Product.seller,
                }
            });
            
            // If the admin reset the timer, the new createdAt will give us positive remaining time.
            // We clear the hasTimedOut flag so the countdown useEffect can restart.
            const now = new Date();
            const orderTime = new Date(pendingOrder.createdAt);
            const limit = pendingOrder.orderConfirmTimeoutSeconds ?? 20;
            const diff = Math.floor((now.getTime() - orderTime.getTime()) / 1000);
            if (limit - diff > 0) {
                setHasTimedOut(false);
            }

        } else {
            navigate("/login")
        }
        setLoading(false);
    }

    const getUserInfo = async () => {
        const res = await axiosInstance.get("/protected/getUserInfo")
        const data = res.data.data;
        if (data) {
            const user = data.user;
            if (!user.isActive) {
                navigate("/login");
                return;
            }
            const userProducts = data.userProducts;
            const userOrders: any[] = data.userOrders;

            setUserInfo({
                walletBalances: user.walletBalances,
                clickRemaining: user.clickRemaining,
                userOrders: userOrders,
                orderCountMessage: user.orderCountMessage,
                orderCountPrompt: user.orderCountPrompt,
                userProducts: userProducts?.map((e: any) => {
                    return {
                        id: e.id,
                        amount: e.amount,
                        productId: e.Product?.id,
                        name: e.Product?.name ?? "",
                        seller: e.Product?.seller ?? "",
                        img: process.env.REACT_APP_FOLDER_URL + "/" + (e.Product?.img ?? ""),
                    }
                }) || []
            })
        } else {
            navigate("/login")
        }
    }

    const createNewOrder = async () => {
        // if (!userInfo?.userProducts?.length || !userInfo?.clickRemaining) {
        //     return false;
        // }
        if (!userInfo?.userProducts?.length) {
            return false;
        }
        let chosenProduct = userInfo?.userProducts[0];

        const res = await axiosInstance.post("/protected/createOrder", {
            productId: chosenProduct.productId,
            amount: chosenProduct.amount,
            id: chosenProduct.id,
        })
        if (res.status === 201) {
            return true;
        } else {
            return false;
        }
    }

    // Mount-only fetch, plus polling to catch admin timer resets.
    useEffect(() => {
        runSafely('OrderConfirmationPage.getPendingOrder', getPendingOrder, () => setLoading(false));
        
        const pollInterval = setInterval(() => {
            getPendingOrder();
        }, 3000);
        
        return () => clearInterval(pollInterval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <AppShell backdrop="/images/bonia/bonia-front-store.webp" backdropOpacity={0.85}>
            {loading && <Loader />}
            <Container>
                <div className='px-5 pt-8 pb-10 flex flex-col gap-5 lg:pt-12'>
                    {/* Same card treatment as /order — design-token border and shadow
                        rather than the borderless white block with the inline
                        border-radius, and one consistent p-5 inset instead of the
                        old p-2/py-4.

                        At lg this card becomes a 2-column grid: order summary left,
                        confirm action panel right (sticky). Below lg, both inner wrappers use
                        `contents` so their box disappears and their children fall back to being
                        direct children of this flex-col, spaced by the existing (unprefixed)
                        gap-8 — i.e. one continuous card on mobile. */}
                    <div className='rounded-lg border border-solid border-brand-300 bg-white shadow-card p-5 flex flex-col gap-8 lg:p-6 lg:grid lg:grid-cols-[1fr_380px] lg:gap-10 lg:items-start'>
                        <div className='contents lg:flex lg:flex-col lg:gap-8'>
                            {/* Product line. Fixed-ratio thumbnail with object-cover, so a
                                non-portrait image is cropped rather than stretched. */}
                            <div className='flex gap-4'>
                                <div className='w-[56px] shrink-0 overflow-hidden rounded-md border border-solid border-brand-300 bg-brand-50'>
                                    <img className='w-full aspect-[4/5] object-cover' src={orderInfo?.Product?.img} alt="" />
                                </div>
                                <div className='flex-1 min-w-0'>
                                    <div className='flex justify-between gap-2'>
                                        <div className='text-brand-900 leading-snug'>{orderInfo?.Product.name}</div>
                                        <Tag className='m-0 shrink-0 h-fit' color={orderInfo?.productType === "premium" ? "magenta" : "cyan"}>{orderInfo?.productType?.toUpperCase()}</Tag>
                                    </div>

                                    <div className='flex justify-between items-baseline mt-2'>
                                        <div className='font-display text-[18px] text-brand-900'>RM {formatNumber(orderInfo?.totalAmount ?? 0)}</div>
                                        <div className='text-[14px] text-brand-600'>x{orderInfo?.unit ?? 1}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Reference details — quiet, hairline-separated from the money below. */}
                            <div className='flex flex-col gap-2 pb-5 border-0 border-b border-solid border-brand-300'>
                                <div className='flex justify-between gap-3'>
                                    <div className='text-left text-[13px] text-brand-600'>{t("orderNo")}</div>
                                    <div className='text-right text-[13px] text-brand-900 break-all'>{orderInfo?.referenceNo}</div>
                                </div>
                                <div className='flex justify-between gap-3'>
                                    <div className='text-left text-[13px] text-brand-600'>{t("masa_operasi")}</div>
                                    <div className='text-right text-[13px] text-brand-900'>{orderInfo?.createdAt ? formatDate(orderInfo?.createdAt) : ""}</div>
                                </div>
                                {timeLeft !== null && (
                                    <div className='flex justify-between gap-3'>
                                        <div className='text-left text-[13px] text-red-500'>Timer</div>
                                        <div className='text-right text-[13px] text-red-500 font-bold'>{timeLeft}s</div>
                                    </div>
                                )}
                            </div>

                            {/* Money. Playfair on the figures, matching every other page.
                                Commission is the one the user is here for, so it carries
                                the accent; the total stays neutral. Both were `text-danger`
                                red before, which read as an error state. */}
                            <div className='flex flex-col gap-3'>
                                <div className='flex justify-between items-baseline gap-3'>
                                    <div className='text-left text-xs uppercase tracking-[0.14em] text-brand-600'>{t("total_reward")}</div>
                                    <div className='text-right font-display text-[20px] text-brand-900'>RM {formatNumber(orderInfo?.totalAmount ?? 0)}</div>
                                </div>
                                <div className='flex justify-between items-baseline gap-3'>
                                    <div className='text-left text-xs uppercase tracking-[0.14em] text-brand-600'>{t("commission")}</div>
                                    <div className='text-right font-display text-[20px] text-accent-600'>RM {formatNumber(orderInfo?.comission ?? 0)}</div>
                                </div>

                                {/* <div className='flex justify-between'>
                                    <div className='text-left text-brand-600'>Nilai Data</div>
                                    <div className='text-right font-bold text-[red]'>RM {formatNumber((orderInfo?.totalAmount ?? 0) * 4)}</div>
                                </div> */}
                            </div>
                        </div>

                        <div className='contents lg:flex lg:sticky lg:top-28'>
                            <Button
                                onClick={() => {
                                    runSafely('OrderConfirmationPage.completeOrder', completeOrder, () => {
                                        setLoading(false);
                                        setButtonClicked(false);
                                    });
                                }}
                                disabled={buttonClicked || hasTimedOut}
                                className={
                                    'w-full text-white bg-brand-800 uppercase tracking-[0.12em] ' +
                                    'hover:!bg-accent-600 hover:!border-accent-600 ' +
                                    'focus-visible:outline focus-visible:outline-2 ' +
                                    'focus-visible:outline-offset-2 focus-visible:outline-accent-500'
                                }
                                size='large'
                            >
                                {t("confirm_order")}
                            </Button>
                        </div>
                    </div>
                </div>
            </Container>
        </AppShell>
    );
}

export default OrderConfirmationPage;